import { html } from 'htm/preact'

export function SchedulerStatus({ schedulerData, overallStats }) {
  if (!schedulerData || !overallStats) return null

  const getJobStatusColor = (status) => {
    switch (status) {
      case 'running':
        return 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
      case 'pending':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100'
      case 'error':
        return 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100'
    }
  }

  const formatDuration = (seconds) => {
    if (seconds < 60) return `${seconds}s`
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    return `${hours}h ${minutes}m`
  }

  const formatTime = (timeString) => {
    if (!timeString) return 'Never'
    return new Date(timeString).toLocaleTimeString()
  }

  return html`
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
      <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h2 class="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <svg class="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
          </svg>
          Scheduler Status
        </h2>
      </div>
      
      <div class="p-6 space-y-6">
        <!-- Scheduler Info -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600 dark:text-gray-400">Status</p>
                <p class="text-lg font-semibold ${schedulerData.running ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}">
                  ${schedulerData.running ? 'Running' : 'Stopped'}
                </p>
              </div>
              <div class="w-3 h-3 rounded-full ${schedulerData.running ? 'bg-green-500' : 'bg-red-500'}"></div>
            </div>
          </div>

          <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600 dark:text-gray-400">Active Jobs</p>
                <p class="text-lg font-semibold text-blue-600 dark:text-blue-400">
                  ${schedulerData.job_count || 0}
                </p>
              </div>
              <svg class="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
          </div>

          <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600 dark:text-gray-400">Uptime</p>
                <p class="text-lg font-semibold text-gray-900 dark:text-white">
                  ${schedulerData.uptime ? formatDuration(schedulerData.uptime) : 'Unknown'}
                </p>
              </div>
              <svg class="w-5 h-5 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
              </svg>
            </div>
          </div>
        </div>

        <!-- Overall Statistics -->
        <div>
          <h3 class="text-md font-medium text-gray-900 dark:text-white mb-4">Overall Statistics</h3>
          <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div class="text-center">
              <p class="text-2xl font-bold text-green-600 dark:text-green-400">
                ${overallStats.total_articles || 0}
              </p>
              <p class="text-sm text-gray-600 dark:text-gray-400">Total Articles</p>
            </div>
            
            <div class="text-center">
              <p class="text-2xl font-bold text-blue-600 dark:text-blue-400">
                ${overallStats.articles_today || 0}
              </p>
              <p class="text-sm text-gray-600 dark:text-gray-400">Today</p>
            </div>
            
            <div class="text-center">
              <p class="text-2xl font-bold text-purple-600 dark:text-purple-400">
                ${overallStats.articles_last_hour || 0}
              </p>
              <p class="text-sm text-gray-600 dark:text-gray-400">Last Hour</p>
            </div>
            
            <div class="text-center">
              <p class="text-2xl font-bold text-orange-600 dark:text-orange-400">
                ${overallStats.active_sources || 0}
              </p>
              <p class="text-sm text-gray-600 dark:text-gray-400">Active Sources</p>
            </div>
          </div>
        </div>

        <!-- Recent Jobs -->
        ${schedulerData.recent_jobs && schedulerData.recent_jobs.length > 0 ? html`
          <div>
            <h3 class="text-md font-medium text-gray-900 dark:text-white mb-4">Recent Jobs</h3>
            <div class="space-y-2">
              ${schedulerData.recent_jobs.slice(0, 5).map(job => html`
                <div key=${job.id} class="flex items-center justify-between py-2 px-3 bg-gray-50 dark:bg-gray-700 rounded">
                  <div class="flex items-center gap-3">
                    <span class="${getJobStatusColor(job.status)} px-2 py-1 rounded-full text-xs font-medium uppercase">
                      ${job.status || 'unknown'}
                    </span>
                    <span class="text-sm font-medium text-gray-900 dark:text-white">
                      ${job.name || job.id}
                    </span>
                  </div>
                  <div class="text-right">
                    <p class="text-xs text-gray-600 dark:text-gray-400">
                      ${job.next_run_time ? html`Next: ${formatTime(job.next_run_time)}` : 'Not scheduled'}
                    </p>
                    ${job.last_run_time && html`
                      <p class="text-xs text-gray-500 dark:text-gray-500">
                        Last: ${formatTime(job.last_run_time)}
                      </p>
                    `}
                  </div>
                </div>
              `)}
            </div>
          </div>
        ` : html`
          <div class="text-center py-8">
            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">No Recent Jobs</h3>
            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
              No jobs have been executed recently.
            </p>
          </div>
        `}
      </div>
    </div>
  `
}